import React from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Users, FolderKanban, Boxes, Ruler, Tag, Settings, Bookmark, FileText, Receipt, LogOut, ShieldCheck, HelpCircle,
} from "lucide-react";
import EmmaAvatar from "./EmmaAvatar";
import { Toaster } from "../components/ui/sonner";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { useAuth } from "../lib/auth";

// v6 F13 §1 — Desktop nav-item (icon + optional label on lg+).
// Compacte pill met donkere active-state — blijft ongewijzigd t.o.v. F12.
const NavItem = ({ to, icon: Icon, label, testId }) => (
  <NavLink
    to={to}
    end
    data-testid={testId}
    className={({ isActive }) =>
      `flex items-center gap-2 px-3 lg:px-4 h-12 rounded-xl transition-all font-medium text-sm ${
        isActive ? "bg-[#2C2C2A] text-white shadow-sm" : "text-[#2C2C2A] hover:bg-[#F0EFEA]"
      }`
    }
  >
    <Icon className="w-4 h-4" />
    <span className="hidden lg:inline">{label}</span>
  </NavLink>
);

// v6 F13 §1 — Mobiele nav-item. Icon-only, gelijke ruimte (flex-1) en een
// comfortabel touch-target van 44px. Active-state = subtiel peach-vlak +
// oranje icoon (rustig, niet dominerend). aria-label voor screenreaders
// aangezien de tekst weggevallen is.
const MobileNavItem = ({ to, icon: Icon, label, testId }) => (
  <NavLink
    to={to}
    end
    data-testid={testId}
    aria-label={label}
    className={({ isActive }) =>
      `flex-1 flex items-center justify-center h-11 rounded-lg transition ${
        isActive
          ? "text-[#D96C4A] bg-[#FFF8F2]"
          : "text-[#4F4F4D] hover:bg-[#F0EFEA] active:bg-[#F0EFEA]"
      }`
    }
  >
    <Icon className="w-[22px] h-[22px]" />
  </NavLink>
);

export default function Layout({ children, topBarExtras = null }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const greeting = React.useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return "Goedemorgen";
    if (h < 18) return "Goedemiddag";
    return "Goedenavond";
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-[#F9F8F6] emma-grain" data-testid="app-shell">
      <header className="sticky top-0 z-40 bg-[#F9F8F6]/85 backdrop-blur-xl border-b border-[#E5E2DC]" data-testid="app-header">
        <div className="max-w-[1500px] mx-auto px-4 sm:px-6 h-20 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
            <div className="w-11 h-11 rounded-2xl bg-[#2C2C2A] flex items-center justify-center text-white">
              <Ruler className="w-5 h-5" />
            </div>
            <div className="leading-tight">
              <div className="font-heading font-bold text-lg text-[#2C2C2A]">
                EmmaMeasure<span className="text-[#D96C4A]">™</span>
              </div>
              <div className="text-[11px] tracking-[0.18em] uppercase text-[#5D7259] font-semibold">
                powered by EmmaForge™
              </div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1" data-testid="primary-nav">
            <NavItem to="/" icon={LayoutDashboard} label="Dashboard" testId="nav-dashboard" />
            <NavItem to="/klanten" icon={Users} label="Klanten" testId="nav-clients" />
            <NavItem to="/projecten" icon={FolderKanban} label="Projecten" testId="nav-projects" />
            <NavItem to="/offertes" icon={FileText} label="Offertes" testId="nav-quotes" />
            <NavItem to="/facturen" icon={Receipt} label="Facturen" testId="nav-invoices" />
            <NavItem to="/prijslijst" icon={Tag} label="Prijslijst" testId="nav-pricecodes" />
            <NavItem to="/sjablonen" icon={Bookmark} label="Sjablonen" testId="nav-templates" />
            <NavItem to="/modules" icon={Boxes} label="Modules" testId="nav-modules" />
            <NavItem to="/instellingen" icon={Settings} label="Instellingen" testId="nav-settings" />
            {/* v6 F42 — "Beta-lijst" nav-item verwijderd uit EmmaMeasure™.
                Beta/Trial/Pro/Partner administratie wordt beheerd via
                het EmmaForge™ Founder Dashboard. Route + backend blijven
                bestaan zodat oude waitlist-data behouden blijft tot
                gecontroleerde migratie naar EmmaForge™. */}
            <NavItem to="/help" icon={HelpCircle} label="Help" testId="nav-help" />
          </nav>

          <div className="flex items-center gap-3" data-testid="emma-greeting-block">
            {topBarExtras}
            <div className="text-right hidden xl:block leading-tight">
              <div className="text-xs text-[#4F4F4D]">{greeting}</div>
              <div className="text-sm font-semibold text-[#2C2C2A]">
                {user?.name ? `Hallo, ${user.name.split(" ")[0]}!` : "Hallo, ik ben Emma!"}
              </div>
            </div>
            {user ? (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="rounded-full ring-2 ring-transparent hover:ring-[#E5E2DC] transition"
                    data-testid="user-menu-btn"
                    aria-label="Accountmenu"
                  >
                    {user.picture_url ? (
                      <img
                        src={user.picture_url}
                        alt={user.name}
                        className="w-11 h-11 rounded-full object-cover border border-[#E5E2DC]"
                      />
                    ) : (
                      <EmmaAvatar size={44} />
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  side="bottom" align="end"
                  className="w-64 p-3"
                  data-testid="user-menu-popover"
                >
                  <div className="flex items-center gap-2 mb-2">
                    {user.picture_url ? (
                      <img src={user.picture_url} alt={user.name} className="w-9 h-9 rounded-full" />
                    ) : (
                      <EmmaAvatar size={36} />
                    )}
                    <div className="leading-tight min-w-0">
                      <div className="text-sm font-semibold text-[#2C2C2A] truncate">{user.name}</div>
                      <div className="text-[11px] text-[#4F4F4D] truncate">{user.email}</div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {user.is_admin && (
                      <span className="text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded-full bg-[#2C2C2A] text-white font-bold flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3" /> Admin
                      </span>
                    )}
                    {user.is_founding_tester && (
                      <span className="text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded-full bg-[#FBE9DF] text-[#7F4124] font-bold">
                        Founding
                      </span>
                    )}
                    {user.is_beta_tester && !user.is_admin && (
                      <span className="text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded-full bg-[#F0EFEA] text-[#2C2C2A] font-bold">
                        Beta tester
                      </span>
                    )}
                  </div>
                  <button
                    onClick={logout}
                    className="w-full h-10 rounded-lg border border-[#E5E2DC] hover:bg-[#F0EFEA] text-sm font-medium flex items-center justify-center gap-2 text-[#2C2C2A]"
                    data-testid="user-menu-logout"
                  >
                    <LogOut className="w-4 h-4" /> Uitloggen
                  </button>
                </PopoverContent>
              </Popover>
            ) : (
              <EmmaAvatar size={44} />
            )}
          </div>
        </div>
        {/* v6 F13 §1 — Mobiele navigatie: 9 iconen gelijk verdeeld over
            volle breedte (flex-1). Geen horizontale scroll meer, iconen
            gecentreerd en van gelijke touch-area. Help-icoon als laatste. */}
        <div className="md:hidden border-t border-[#E5E2DC] bg-white" data-testid="mobile-nav">
          <div className="flex items-center gap-0.5 px-1.5 py-1.5">
            <MobileNavItem to="/" icon={LayoutDashboard} label="Dashboard" testId="mnav-dashboard" />
            <MobileNavItem to="/klanten" icon={Users} label="Klanten" testId="mnav-clients" />
            <MobileNavItem to="/projecten" icon={FolderKanban} label="Projecten" testId="mnav-projects" />
            <MobileNavItem to="/offertes" icon={FileText} label="Offertes" testId="mnav-quotes" />
            <MobileNavItem to="/facturen" icon={Receipt} label="Facturen" testId="mnav-invoices" />
            <MobileNavItem to="/prijslijst" icon={Tag} label="Prijslijst" testId="mnav-pricecodes" />
            <MobileNavItem to="/sjablonen" icon={Bookmark} label="Sjablonen" testId="mnav-templates" />
            <MobileNavItem to="/modules" icon={Boxes} label="Modules" testId="mnav-modules" />
            <MobileNavItem to="/instellingen" icon={Settings} label="Instellingen" testId="mnav-settings" />
            {/* v6 F42 — Mobile "Beta-lijst" nav-item verwijderd (zie desktop-nav toelichting). */}
            <MobileNavItem to="/help" icon={HelpCircle} label="Help &amp; handleiding" testId="mnav-help" />
          </div>
        </div>
      </header>

      <main className="flex-1 relative z-10" data-testid="app-main">
        <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-8">
          <div key={location.pathname} className="fade-up">{children}</div>
        </div>
      </main>

      <footer className="bg-[#F0EFEA] border-t border-[#E5E2DC] py-8 text-center text-sm text-[#4F4F4D]" data-testid="app-footer">
        <div className="max-w-[1500px] mx-auto px-6">
          <p className="font-medium">
            EmmaMeasure<span className="text-[#D96C4A]">™</span> — developed by{" "}
            <span className="text-[#2C2C2A] font-semibold">EmmaForge™</span>
          </p>
          <p className="text-xs mt-1">
            Snel meten, helder offreren en professioneel opvolgen.
          </p>
        </div>
      </footer>

      <Toaster richColors position="top-right" />
    </div>
  );
}
